import React from 'react';
import { Box } from '@mui/material';
import {
  SparkDynamicOutput,
  SparkComponentSearchField,
} from 'sharedComponents/UI';
import EditableTitleSkeleton from 'sharedComponents/Skeleton/EditableTitleSkeleton';
import { useBoardNodeTypes } from 'hooks/EntityTypes/useBoardNodeTypes';
import NodeSpecificOutput from './TestAccordion/NodeSpecificOutput';
import { useTransformPromptReferences } from './TestAccordion/utils';
import GraphNodeSkeleton from 'sharedComponents/Skeleton/GraphNodeSkeleton';
import MermaidNodeSkeleton from 'sharedComponents/Skeleton/MermaidNodeSkeleton';
import { useComponentSearch } from 'hooks/common';

type SparkTestOutputContainerProps = {
  output: string | undefined;
  isParentLoading?: boolean;
  outputTypeID?: string | null;
  onError: (errorMessage: string) => void;
  outputError: string | null;
};

function SparkTestOutputContainer({
  isParentLoading,
  output,
  outputTypeID,
  onError,
  outputError,
}: SparkTestOutputContainerProps) {
  const sparkTestSearchRef = React.useRef<HTMLElement | null>(null);
  const { idMap, isLoading: isLoadingBoardNodes } = useBoardNodeTypes();
  const transformReference = useTransformPromptReferences();
  const nodeType = idMap?.[outputTypeID || ''];
  const isLoading = isParentLoading || isLoadingBoardNodes;
  const [searchText, setSearchText] = React.useState('');

  const {
    handleSearch,
    previousMatch,
    nextMatch,
    totalMatches,
    currentIndex,
    clearHighlights,
  } = useComponentSearch(sparkTestSearchRef.current || undefined);

  const handleClose = () => {
    clearHighlights();
    setSearchText('');
  };

  const nodeTypeSpecificShimmer = React.useMemo(() => {
    if (nodeType?.attributes?.key === 'mermaid') {
      return <MermaidNodeSkeleton />;
    }
    if (nodeType?.attributes?.key === 'graph') {
      return <GraphNodeSkeleton />;
    }
    return [1, 2, 3, 4, 5].map((val) => (
      <EditableTitleSkeleton
        skeletonStyles={{ width: '100%', marginX: 1.1 }}
        boxStyles={{
          paddingBottom: 1,
        }}
        key={val}
      />
    ));
  }, [nodeType]);

  return (
    <Box
      display='flex'
      border={(theme) => `1px solid ${theme.palette.borderColor}`}
      borderRadius={2}
      flexDirection='column'
      margin={2}
      position='relative'
      overflow='hidden'
    >
      <Box
        sx={{
          padding: 2,
          bgcolor: 'white',
          zIndex: 1,
        }}
      >
        <SparkComponentSearchField
          value={searchText}
          onChange={(val) => {
            setSearchText(val);
            handleSearch(val);
          }}
          currentIndex={currentIndex}
          totalMatches={totalMatches}
          onClose={handleClose}
          onNextClick={nextMatch}
          onPreviousClick={previousMatch}
          showCancelButton={false}
        />
      </Box>

      {isLoading ? (
        nodeTypeSpecificShimmer
      ) : (
        <Box
          ref={sparkTestSearchRef}
          sx={{
            userSelect: 'text',
            padding: nodeType?.attributes?.key === 'text' ? 0 : 2,
          }}
          maxHeight='calc(100vh - 30rem)'
          overflow='auto'
        >
          {!nodeType ? (
            <SparkDynamicOutput
              output={output || ''}
              transformReference={transformReference}
            />
          ) : (
            <NodeSpecificOutput
              nodeType={nodeType}
              output={output || ''}
              onError={onError}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export default SparkTestOutputContainer;
